import AccordionSection from "components/common/AccordionSection";
import AiBanner from "components/common/banners/AiBanner";
import ROIBanner from "components/common/banners/ROIBanner";
import CTABannerRelay from "components/common/footer-banners/CTABannerRelay";
import HeroLayout1 from "components/common/hero/HeroLayout1";
import {
  CTA_BUTTON_COPY_3,
  CTA_BUTTON_COPY_7,
  LIVE_DEMO_LINK,
} from "components/data/landing-page-data";
import GlobalLayout from "components/page/GlobalLayout";
import TabberSection from "components/rui/page-components/TabberSection";
import { graphql, useStaticQuery } from "gatsby";
import React from "react";
import AiSpecs from "./subcomponents/AiSpecs";
import TestimonySection from "components/testimony/TestimonySection";
import { Helmet } from "react-helmet";

const imageProps = {
  imgStyles: {
    borderRadius: "12px",
    width: "669px",
    maxWidth: "calc(100% - 2.5rem)",
    maxHeight: "416px",
  },
};

const AiPage = () => {
  const imageData = useStaticQuery(graphql`
    query {
      hero: file(
        relativePath: { eq: "components/ai/assets/ai-page-hero.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      tab1: file(
        relativePath: { eq: "components/ai/assets/video-search-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      tab2: file(
        relativePath: { eq: "components/ai/assets/face-rec-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      tab3: file(relativePath: { eq: "components/ai/assets/lpr-min.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      tab4: file(
        relativePath: { eq: "components/ai/assets/audio-analytics-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      acc1: file(
        relativePath: { eq: "components/ai/assets/line-crossing-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      acc2: file(
        relativePath: { eq: "components/ai/assets/heatmaps-min.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      acc3: file(relativePath: { eq: "components/ai/assets/sensors.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      acc4: file(
        relativePath: { eq: "components/ai/assets/integrations.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
      testimony: file(
        relativePath: { eq: "components/ai/assets/testimony.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, quality: 100)
        }
      }
    }
  `);

  return (
    <GlobalLayout dark color="var(--primary-bg)">
      <Helmet>
        <title>
          Smarter, Faster Security with AI Video Analytics and Insights |
          Rhombus
        </title>
        <meta
          name="description"
          content="Harness the power of AI video analytics, environmental insights, and configurable alerts for smarter physical security. See a live demo of the Rhombus cloud platform for faster investigations and streamlined operations."
        />
        <meta
          name="thumbnail"
          content="https://rhombus.com/img/MetaTag-ai.png"
        />
        <meta
          property="og:image"
          content="https://rhombus.com/img/MetaTag-ai.png"
        />
      </Helmet>
      <HeroLayout1
        data={{
          image: imageData.hero,
          preTitle: "Ai Analytics",
          title: "Save Time with AI Video Analytics and Insights",
          description:
            "Spend less time searching for footage and more time securing spaces. Rhombus' AI video analytics, audio detections, and environmental insights speed up investigations, enhance safety, accelerate emergency response, and help you stay ahead of threats.",
          button1: {
            text: CTA_BUTTON_COPY_7,
            path: LIVE_DEMO_LINK,
            type: "primary",
          },
        }}
        dark
      />
      <TabberSection
        sectionTitle={"Turn Data into Security Intelligence"}
        tabContent={[
          {
            tabTitle: "AI Video Search",
            preTitle: "rapid investigations",
            title: "AI Video Search",
            blurb:
              "Revolutionize investigations with fast, actionable insights powered by natural language processing. Describe an event, like person delivering a package, and AI Search scans the cameras you choose to surface the relevant footage in seconds.",
            imageData: imageData.tab1,
            alt: "ai video analytics using natural language machine learning models",
          },
          {
            tabTitle: "Facial Recognition",
            preTitle: "Streamline Identification",
            title: "Advanced Facial Recognition",
            blurb:
              "Say goodbye to manual scrubbing—instantly search for matched faces across your video streams, get notified about persons-of-interest in your locations, and detect unauthorized visitors with greater confidence.",
            imageData: imageData.tab2,
            alt: "facial recognition on a video analytics platform",
          },
          {
            tabTitle: "License Plate Recognition",
            preTitle: "Secure Building Perimeters",
            title: "License Plate Recognition",
            blurb:
              "Monitor parking lots, garages, and outdoor areas with precision. Instantly search by plate number with AI powered video analytics, detect unauthorized vehicles, and automate vehicle logs.",
            imageData: imageData.tab3,
            alt: "license plate recognition across multiple cameras",
          },
          {
            tabTitle: "Audio Analytics",
            preTitle: "Navigate by Noise",
            title: "Audio Analytics",
            blurb:
              "Layer security footage with intelligent audio analytics. Receive real-time alerts triggered by abnormal audio events, detect glass breaks, and broadcast audio messages through two-way communication.",
            imageData: imageData.tab4,
            alt: "audio analytics with ai video analytics software",
          },
        ]}
      />
      <AiBanner />
      <AiSpecs />
      <AccordionSection
        data={{
          title: "Operational Efficiency with Video and IoT Intelligence",
          items: [
            {
              title: "Track Real-Time Movement and Monitor Capacity",
              p: "Get accurate occupancy and movement insights to optimize foot and vehicle traffic, manage capacity, and make smarter space decisions - no manual counting needed.",
              img: imageData.acc1,
              alt: "objection detection and artificial intelligence for operations",
              ...imageProps,
            },
            {
              title: "Optimize Guest Experience & Equipment Use",
              p: "Maximize site efficiency with real-time heat maps to instantly identify high-traffic areas, uncover underused areas, and make smarter decisions around maintenance, layout, and resource allocation.",
              img: imageData.acc2,
              alt: "intelligent video analytics with heat maps",
              ...imageProps,
            },
            {
              title: "Monitor Beyond What Cameras Can See",
              p: "Monitor temperature, humidity, air quality, smoke, and vape in real-time to safeguard people and assets. Get instant alerts for access violations, open doors, or unusual motion—ideal for areas where cameras can't go.",
              img: imageData.acc3,
              alt: "IoT insights with video footage from security cameras",
              ...imageProps,
            },
            {
              title: "Effortlessly Connect Existing Stack for Richer Insights",
              p: "Integrate third-party analytics and BI tools directly into your camera feeds for a unified operational context for a single pane of glass view.",
              img: imageData.acc4,
              alt: "integrations with video analytics software",
              ...imageProps,
            },
          ],
        }}
      />
      <ROIBanner />
      <TestimonySection
        imageSrc={imageData.testimony}
        quote={
          <>
            We've seen a <b>40% reduction</b> in security incidents and a{" "}
            <b>30% decrease</b> in time spent reviewing footage, thanks to
            Rhombus' intelligent video analytics and cloud-based access.
          </>
        }
        name={"Sr IT Manager"}
        company={"Enterprise Storage & Warehousing Company"}
        imageAlt={"video analytics solutions for security systems"}
      />
      <CTABannerRelay
        title="Explore Rhombus for your facilities."
        blurb="Discover how our innovative solutions can enhance your security and operational efficiency today."
        buttonText={CTA_BUTTON_COPY_3}
      />
    </GlobalLayout>
  );
};

export default AiPage;
